/** @format */

import { useEffect, useState } from "react";
import { fetchAllDamData, fetchClientSettings } from "../api-helper/damapi";
import Decimal from "decimal.js";
import "../Style/summaryCallOption.css";

function formatNumberGR(value) {
  return new Intl.NumberFormat("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function getLocalHoursInMonth(year, month) {
  const start = new Date(year, month, 1, 0, 0, 0);
  const end = new Date(year, month + 1, 1, 0, 0, 0);
  const diffMs = end.getTime() - start.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours;
}

function SummaryCallOption({ email, selectedMonth }) {
  const [strike, setStrike] = useState(new Decimal(0));
  const [premium, setPremium] = useState(new Decimal(0));
  const [quantity, setQuantity] = useState(new Decimal(0));

  const [averageMcpMarch, setAverageMcpMarch] = useState(0);
  const [pnlUnitary, setPnlUnitary] = useState(0);
  const [pnl, setPnl] = useState(0);
  const [totalWithPremium, setTotalWithPremium] = useState(0);
  const [availableMonths, setAvailableMonths] = useState([]);

  const [hoursInMonth, setHoursInMonth] = useState(0);
  const [premiumcost, setpremiumcost] = useState(0);
  const [totalHedging, setTotalHedging] = useState(new Decimal(0));
  const [difference, setDifference] = useState(new Decimal(0));

  useEffect(() => {
    async function loadData() {
      if (!email) return;

      const [allData, clientData] = await Promise.all([
        fetchAllDamData(),
        fetchClientSettings(email),
      ]);
      const months = clientData.settings.map((s) => s.month);
      setAvailableMonths(months);

      if (!selectedMonth) return;
      const settings = clientData.settings.find(
        (s) => s.month === selectedMonth
      );

      const strikeValue = new Decimal(settings.strike);
      const premiumValue = new Decimal(settings.premium);
      const quantityValue = new Decimal(settings.quantity);

      setStrike(strikeValue);
      setPremium(premiumValue);
      setQuantity(quantityValue);

      const [yearStr, monthStr] = selectedMonth.split("-");
      const year = parseInt(yearStr);
      const month = parseInt(monthStr) - 1; // zero-based for JS

      const filtered = allData.filter((entry) =>
        entry.date.startsWith(selectedMonth)
      );

      const HOURS_IN_MONTH = getLocalHoursInMonth(year, month);
      setHoursInMonth(HOURS_IN_MONTH);

      const allValues = filtered.flatMap((entry) =>
        entry.values.map((v) => v.MCP)
      );

      const totalHours = allValues.length;
      const sum = allValues.reduce((acc, val) => acc + val, 0);
      const average = new Decimal(sum).dividedBy(totalHours);
      setAverageMcpMarch(average);
      const diff = average.minus(strikeValue);

      const penaltyPercent = new Decimal(settings.penaltyPercentage);
      const penaltyFactor = penaltyPercent.dividedBy(100);
      const unitary = diff.isPositive() ? diff : diff.times(penaltyFactor);

      const total = unitary.times(quantityValue).times(HOURS_IN_MONTH);
      const premiumCost = -premiumValue
        .times(quantityValue)
        .times(HOURS_IN_MONTH);

      const finalTotal = total.plus(premiumCost);

      const unitaryHedging = diff.isPositive() ? diff : diff.dividedBy(1);
      const totalHedging = unitaryHedging
        .times(quantityValue)
        .times(HOURS_IN_MONTH);

      setpremiumcost(premiumCost);
      setPnlUnitary(unitary);
      setPnl(total);
      setTotalWithPremium(finalTotal);
      setTotalHedging(totalHedging);
      setDifference(finalTotal.minus(totalHedging));
    }

    loadData();
  }, [email, selectedMonth]);

  return (
    <div className='summary-container'>
      <div className='summary-block'>
        <table className='styled-table'>
          <tbody>
            <tr className='section-title'>
              <td colSpan='2'>Fundamentals of Call Option</td>
            </tr>
            <tr>
              <td>Strike Price Call Option</td>
              <td>{formatNumberGR(strike)} €/MWh</td>
            </tr>
            <tr>
              <td>Premium fee</td>
              <td>{formatNumberGR(premium)} €/MWh</td>
            </tr>
            <tr>
              <td>Baseload Capacity</td>
              <td>{formatNumberGR(quantity)} MW</td>
            </tr>
            <tr>
              <td>Hours of month</td>
              <td>{hoursInMonth} Hours</td>
            </tr>
            <tr>
              <td>Call Option Consumption</td>
              <td>{quantity.times(hoursInMonth).toFixed(0)} MWh</td>
            </tr>

            <tr className='section-title'>
              <td colSpan='2'>P&amp;L Call Option</td>
            </tr>
            <tr>
              <td>MtD DAM Price</td>
              <td>{formatNumberGR(averageMcpMarch)} €/MWh</td>
            </tr>
            <tr>
              <td>P&amp;L (unitary)</td>
              <td>{formatNumberGR(pnlUnitary)} €/MWh</td>
            </tr>
            <tr>
              <td>P&amp;L</td>
              <td className={pnl >= 0 ? "positive" : "negative"}>
                {formatNumberGR(pnl)} €
              </td>
            </tr>
            <tr>
              <td>Premium cost</td>
              <td className={premiumcost >= 0 ? "positive" : "negative"}>
                {formatNumberGR(premiumcost)} €
              </td>
            </tr>
            <tr>
              <td>Total P&amp;L incl premium cost</td>
              <td className={totalWithPremium >= 0 ? "positive" : "negative"}>
                {formatNumberGR(totalWithPremium)} €
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className='summary-block2'>
        <h2>Hedging with Option vs Hedging with Fixed Price</h2>
        <table>
          <tbody>
            <tr>
              <td>Total P&amp;L Call Option</td>
              <td>{formatNumberGR(totalWithPremium)} €</td>
            </tr>
            <tr>
              <td>Total P&amp;L Fixed Price</td>
              <td>{formatNumberGR(totalHedging)} €</td>
            </tr>
            <tr>
              <td>
                <strong>Difference</strong>
              </td>
              <td className={difference >= 0 ? "positive" : "negative"}>
                <strong>{formatNumberGR(difference)} €</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SummaryCallOption;
